import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { generateKeyBetween } from "fractional-indexing";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useAuth } from "../auth/context";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { ShortcutHelp } from "../components/ShortcutHelp";
import { SearchModal, type SearchHit } from "../components/SearchModal";
import { TodayView } from "../components/TodayView";
import { SyncBadge } from "../components/SyncBadge";
import { ReauthBanner } from "../components/ReauthBanner";
import { TreeView } from "../components/TreeView";
import { Gantt } from "../components/Gantt";
import { InstallButton } from "../components/InstallButton";
import { ItemDetail, ShareSection } from "../components/ItemDetail";
import { Modal } from "../components/Modal";
import { NotificationButton } from "../components/NotificationButton";
import { NotificationBell } from "../components/NotificationBell";
import { ClipboardPopover } from "../components/ClipboardPopover";
import { SelectionProvider, useSelection } from "../components/selection";
import { UndoButton, UndoProvider } from "../components/undo";
import {
  CANVAS_PREFIX,
  FOLDER_PREFIX,
  GROUP_PREFIX,
  ITEM_PREFIX,
  NOTE_PREFIX,
  SCENE_PREFIX,
  type Canvas,
  type Folder,
  type Group,
  type Item,
  type Note,
  type Scene,
} from "../replicache/keys";
import { NotepadView } from "../components/NotepadView";
import { CanvasView } from "../components/CanvasView";
import { ExcalidrawView } from "../components/ExcalidrawView";
import { parseScene } from "../scenes/excalidrawTypes";
import { useReplicache, useSubscribe } from "../replicache/context";
import { uuidv7 } from "../replicache/uuid";
import { revokeShare } from "../shares/api";
import { SharedWithPanel } from "../shares/SharedWithPanel";
import { ActivityWatchToggle } from "../shares/ActivityWatchToggle";
import { lookupUser, type UserBrief } from "../contacts/api";

type ViewMode = "tree" | "gantt";
const VIEW_KEY = "johndo:view";
const SIDEBAR_KEY = "johndo:sidebarCollapsed";
// Shared between the Tree and Gantt views.  When on, both views collapse
// to just starred tasks (and their parent chain) across every accessible
// group, with group dividers separating the sections.
const FOCUS_MODE_KEY = "johndo:focusMode";

export function Home() {
  const { state, signOut } = useAuth();
  const rep = useReplicache();

  const groups = useSubscribe<Group[]>(
    rep,
    async (tx) => {
      const rows: Group[] = [];
      for await (const value of tx.scan({ prefix: GROUP_PREFIX }).values()) {
        rows.push(value as Group);
      }
      rows.sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
      return rows;
    },
    [],
  );

  const folders = useSubscribe<Folder[]>(
    rep,
    async (tx) => {
      const rows: Folder[] = [];
      for await (const value of tx.scan({ prefix: FOLDER_PREFIX }).values()) {
        rows.push(value as Folder);
      }
      rows.sort((a, b) =>
        a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
      );
      return rows;
    },
    [],
  );

  const notes = useSubscribe<Note[]>(
    rep,
    async (tx) => {
      const rows: Note[] = [];
      for await (const value of tx.scan({ prefix: NOTE_PREFIX }).values()) {
        rows.push(value as Note);
      }
      rows.sort((a, b) =>
        a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
      );
      return rows;
    },
    [],
  );

  const canvases = useSubscribe<Canvas[]>(
    rep,
    async (tx) => {
      const rows: Canvas[] = [];
      for await (const value of tx.scan({ prefix: CANVAS_PREFIX }).values()) {
        rows.push(value as Canvas);
      }
      rows.sort((a, b) =>
        a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
      );
      return rows;
    },
    [],
  );

  const scenes = useSubscribe<Scene[]>(
    rep,
    async (tx) => {
      const rows: Scene[] = [];
      for await (const value of tx.scan({ prefix: SCENE_PREFIX }).values()) {
        rows.push(value as Scene);
      }
      rows.sort((a, b) =>
        a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
      );
      return rows;
    },
    [],
  );

  const [view, setView] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return stored === "gantt" ? "gantt" : "tree";
  });
  useEffect(() => {
    localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  });
  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  const [focusMode, setFocusMode] = useState<boolean>(() => {
    return localStorage.getItem(FOCUS_MODE_KEY) === "1";
  });
  useEffect(() => {
    localStorage.setItem(FOCUS_MODE_KEY, focusMode ? "1" : "0");
  }, [focusMode]);

  // For Tree view + focus mode: which groups have at least one starred
  // item, so we know which TreeViews to stack.  Kept as a sorted id array
  // so useSubscribe's shallow-equal diff stays cheap.
  const focusedGroupIds = useSubscribe<string[]>(
    rep,
    async (tx) => {
      const set = new Set<string>();
      for await (const value of tx.scan({ prefix: ITEM_PREFIX }).values()) {
        const it = value as Item;
        if (it.isFocused) set.add(it.groupId);
      }
      return [...set].sort();
    },
    [],
  );
  const focusedGroupIdSet = new Set(focusedGroupIds);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [notesView, setNotesView] = useState(false);
  const [todayView, setTodayView] = useState(false);
  const [draftNote, setDraftNote] = useState(false);
  const [selectedCanvasId, setSelectedCanvasId] = useState<string | null>(null);
  const [canvasView, setCanvasView] = useState(false);
  const [draftCanvas, setDraftCanvas] = useState(false);
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [sceneView, setSceneView] = useState(false);
  const [draftScene, setDraftScene] = useState(false);
  const selectedGroup = groups.find((g) => g.id === selectedGroupId) ?? null;
  const selectedNote = notes.find((n) => n.id === selectedNoteId) ?? null;
  const selectedCanvas = canvases.find((c) => c.id === selectedCanvasId) ?? null;
  const selectedScene = scenes.find((s) => s.id === selectedSceneId) ?? null;

  const onSelectGroup = (id: string | null) => {
    setSelectedGroupId(id);
    if (id !== null) {
      setTodayView(false);
      setSelectedNoteId(null);
      setNotesView(false);
      setDraftNote(false);
      setSelectedCanvasId(null);
      setCanvasView(false);
      setDraftCanvas(false);
      setSelectedSceneId(null);
      setSceneView(false);
      setDraftScene(false);
    }
  };
  const onNotesView = () => {
    setNotesView(true);
    setTodayView(false);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setSelectedCanvasId(null);
    setCanvasView(false);
    setDraftCanvas(false);
    setSelectedSceneId(null);
    setSceneView(false);
    setDraftScene(false);
  };
  const onTodayView = () => {
    setTodayView(true);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setNotesView(false);
    setDraftNote(false);
    setSelectedCanvasId(null);
    setCanvasView(false);
    setDraftCanvas(false);
  };
  const onSelectNote = (id: string | null) => {
    setSelectedNoteId(id);
    if (id !== null) {
      setTodayView(false);
      setSelectedGroupId(null);
      setSelectedCanvasId(null);
      setCanvasView(false);
      setSelectedSceneId(null);
      setSceneView(false);
      setDraftScene(false);
    }
  };
  const handleCreateNote = () => {
    setNotesView(true);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setSelectedCanvasId(null);
    setCanvasView(false);
    setSelectedSceneId(null);
    setSceneView(false);
    setDraftScene(false);
    setDraftNote(true);
  };
  const onCanvasView = () => {
    setCanvasView(true);
    setTodayView(false);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setNotesView(false);
    setDraftNote(false);
    setSelectedCanvasId(null);
    setSelectedSceneId(null);
    setSceneView(false);
    setDraftScene(false);
  };
  const onSelectCanvas = (id: string | null) => {
    setSelectedCanvasId(id);
    if (id !== null) {
      setTodayView(false);
      setSelectedGroupId(null);
      setSelectedNoteId(null);
      setNotesView(false);
      setSelectedSceneId(null);
      setSceneView(false);
      setDraftScene(false);
    }
  };
  const handleCreateCanvas = () => {
    setCanvasView(true);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setNotesView(false);
    setDraftNote(false);
    setSelectedCanvasId(null);
    setSelectedSceneId(null);
    setSceneView(false);
    setDraftScene(false);
    setDraftCanvas(true);
  };
  const onSceneView = () => {
    setSceneView(true);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setNotesView(false);
    setDraftNote(false);
    setSelectedCanvasId(null);
    setCanvasView(false);
    setDraftCanvas(false);
    setSelectedSceneId(null);
  };
  const onSelectScene = (id: string | null) => {
    setSelectedSceneId(id);
    if (id !== null) {
      setSelectedGroupId(null);
      setSelectedNoteId(null);
      setNotesView(false);
      setSelectedCanvasId(null);
      setCanvasView(false);
    }
  };
  const handleCreateScene = () => {
    setSceneView(true);
    setSelectedGroupId(null);
    setSelectedNoteId(null);
    setNotesView(false);
    setDraftNote(false);
    setSelectedCanvasId(null);
    setCanvasView(false);
    setDraftCanvas(false);
    setSelectedSceneId(null);
    setDraftScene(true);
  };

  // `?scene=<id>` deep-link — used by the "Open drawing" link on
  // /shared-with-me for shared-in Excalidraw scenes.  Opens the scene directly
  // if it's already synced to this device, otherwise opens the Excalidraw list
  // where it'll appear within a pull cycle.  The param is stripped either way
  // so back-navigation doesn't re-trigger it.
  const [sceneLinkParams, setSceneLinkParams] = useSearchParams();
  const sceneLinkId = sceneLinkParams.get("scene");
  useEffect(() => {
    if (!sceneLinkId) return;
    if (scenes.some((s) => s.id === sceneLinkId)) {
      onSelectScene(sceneLinkId);
    } else {
      onSceneView();
    }
    setSceneLinkParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("scene");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneLinkId, scenes]);

  // Deep-link target: an item id we want the tree to reveal (expand its
  // ancestor chain and scroll into view).  Set by ItemDeepLink when a
  // `?item=` URL lands; consumed by TreeView once it's scrolled, which
  // calls back with null.
  const [revealItemId, setRevealItemId] = useState<string | null>(null);

  // ⌘K / Ctrl+K quick switcher.
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onSearchJump = (hit: SearchHit) => {
    if (hit.kind === "item") {
      // Same dance as ItemDeepLink: tree view, focus mode off, group
      // selected, then reveal (expand ancestors + scroll into view).
      setView("tree");
      setFocusMode(false);
      onSelectGroup(hit.groupId);
      setRevealItemId(hit.id);
    } else if (hit.kind === "group") {
      setView("tree");
      onSelectGroup(hit.id);
    } else if (hit.kind === "note") {
      onSelectNote(hit.id);
    } else {
      onSelectCanvas(hit.id);
    }
  };
  const clearReveal = () => setRevealItemId(null);

  // Transient banner shown when ItemDeepLink can't resolve a `?item=` id —
  // the task was deleted, or hasn't synced to this device.  Auto-dismisses
  // so a stale message doesn't linger after the user moves on.
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  useEffect(() => {
    if (!deepLinkError) return;
    const id = window.setTimeout(() => setDeepLinkError(null), 5000);
    return () => window.clearTimeout(id);
  }, [deepLinkError]);

  if (state.status === "loading") {
    return (
      <main className="mx-auto flex min-h-full max-w-3xl items-center justify-center p-8">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </main>
    );
  }
  if (state.status === "unauthenticated") {
    return <Navigate to="/signin" replace />;
  }

  return (
    <UndoProvider>
    <SelectionProvider>
      <FocusHotkey />
      <ItemDeepLink
        onResolveGroup={setSelectedGroupId}
        onReveal={setRevealItemId}
        onForceTreeView={() => setView("tree")}
        onExitFocusMode={() => setFocusMode(false)}
        onNotFound={() =>
          setDeepLinkError(
            "That task isn't available — it may have been deleted, or hasn't synced to this device yet.",
          )
        }
      />
      <ReauthBanner />
      <ShortcutHelp />
      {searchOpen && (
        <SearchModal onClose={() => setSearchOpen(false)} onJump={onSearchJump} />
      )}
      <div className="flex min-h-full flex-col">
        <header className="flex flex-wrap items-baseline justify-between gap-y-2 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3">
            <img
              src="/seneca-logo.svg"
              alt="Seneca"
              className="h-7 w-auto sm:h-8 dark:hidden"
            />
            <img
              src="/seneca-logo-dark.svg"
              alt="Seneca"
              className="hidden h-7 w-auto sm:h-8 dark:block"
            />
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              title="Search (⌘K)"
              aria-label="Search"
              className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
            >
              🔍<span className="ml-1 hidden sm:inline">⌘K</span>
            </button>
            <SyncBadge />
            <UndoButton />
          </div>

          <div className="flex items-center gap-2">
            <ViewToggle view={view} onView={setView} />
            <button
              type="button"
              onClick={() => setFocusMode((v) => !v)}
              aria-pressed={focusMode}
              title="Focus mode — show only starred tasks (and their parents) across every group"
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                focusMode
                  ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
                  : "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <span aria-hidden className={focusMode ? "text-amber-500" : "text-slate-400 dark:text-slate-500"}>★</span>
              Focus
            </button>
          </div>

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <button
              type="button"
              onClick={() => window.location.reload()}
              title="Reload the app — pending edits stay in IndexedDB across the reload"
              aria-label="Refresh app"
              className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              ↻ Refresh
            </button>
            <InstallButton />
            <ClipboardButton
              accessToken={
                (state.status === "authenticated" ||
                  state.status === "offline") &&
                state.session
                  ? state.session.access_token
                  : null
              }
            />
            <NotificationBell />
            <NotificationButton />
            {(state.status === "authenticated" ||
              state.status === "offline" ||
              state.status === "reauth_required") &&
              state.user && (
                <Link
                  to="/settings"
                  title="Settings"
                  aria-label="Settings"
                  className="inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </Link>
              )}
            {(state.status === "authenticated" ||
              state.status === "offline" ||
              state.status === "reauth_required") &&
              state.user && (
                <Link
                  to="/settings"
                  className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  title={`Account settings — ${state.user.email}`}
                >
                  {state.user.handle ? (
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      @{state.user.handle}
                    </span>
                  ) : (
                    <span className="font-medium text-slate-700 dark:text-slate-200 underline">
                      Claim handle
                    </span>
                  )}
                </Link>
              )}
            {(state.status === "authenticated" ||
              state.status === "offline") && (
              <>
                <Link
                  to="/contacts"
                  className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  Contacts
                </Link>
                <Link
                  to="/shared-with-me"
                  className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  Shared with me
                </Link>
                <Link
                  to="/reports"
                  className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  title="View or submit bug / feature reports"
                >
                  Reports
                </Link>
                <Link
                  to="/archive"
                  className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  title="Browse and restore archived items"
                >
                  Archive
                </Link>
              </>
            )}
            {(state.status === "authenticated" ||
              state.status === "offline") &&
              state.user?.is_admin && (
                <Link
                  to="/admin"
                  className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  Admin
                </Link>
              )}
            {state.status !== "reauth_required" && (
              <button
                onClick={() => void signOut()}
                className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                Sign out
              </button>
            )}
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          {view === "tree" ? (
            <div
              className={`grid min-w-0 flex-1 grid-cols-1 grid-rows-[auto_1fr] divide-x divide-slate-200 dark:divide-slate-700 sm:grid-rows-1 ${
                sidebarCollapsed
                  ? "sm:grid-cols-[2.5rem_minmax(0,1fr)]"
                  : "sm:grid-cols-[16rem_minmax(0,1fr)]"
              }`}
            >
              <GroupsSidebar
                groups={groups}
                folders={folders}
                selectedId={selectedGroupId}
                isNotesView={notesView || selectedNoteId !== null}
                isCanvasView={canvasView || selectedCanvasId !== null}
                isSceneView={sceneView || selectedSceneId !== null}
                currentUserId={
                  (state.status === "authenticated" ||
                    state.status === "offline") &&
                  state.user
                    ? state.user.id
                    : ""
                }
                accessToken={
                  (state.status === "authenticated" ||
                    state.status === "offline") &&
                  state.session
                    ? state.session.access_token
                    : null
                }
                onSelect={onSelectGroup}
                isTodayView={todayView}
                onTodayView={onTodayView}
                onNotesView={onNotesView}
                onCreateNote={handleCreateNote}
                onCanvasView={onCanvasView}
                onCreateCanvas={handleCreateCanvas}
                onSceneView={onSceneView}
                onCreateScene={handleCreateScene}
                collapsed={sidebarCollapsed}
                onToggleCollapsed={() => setSidebarCollapsed((c) => !c)}
              />
              {focusMode ? (
                // Focus mode: stack one TreeView per group that has a
                // starred item.  Each section's `h2` doubles as the
                // group divider — the user sees ★ tasks (and their
                // parents for context) regardless of which group is
                // currently selected in the sidebar.
                <FocusTreeStack
                  groups={groups.filter((g) => focusedGroupIdSet.has(g.id))}
                  onTurnOff={() => setFocusMode(false)}
                  revealItemId={revealItemId}
                  onRevealed={clearReveal}
                />
              ) : todayView ? (
                <ErrorBoundary inline label="today">
                  <TodayView
                    groups={groups}
                    onJumpToItem={(itemId, groupId) => {
                      setView("tree");
                      onSelectGroup(groupId);
                      setRevealItemId(itemId);
                    }}
                  />
                </ErrorBoundary>
              ) : selectedCanvas ? (
                <ErrorBoundary inline label="canvas">
                  <CanvasView
                    canvas={selectedCanvas}
                    onClose={() => onSelectCanvas(null)}
                  />
                </ErrorBoundary>
              ) : canvasView ? (
                <CanvasOverview
                  canvases={canvases}
                  onSelectCanvas={onSelectCanvas}
                  showDraft={draftCanvas}
                  onDraftDismiss={() => setDraftCanvas(false)}
                />
              ) : selectedScene ? (
                <ExcalidrawView
                  scene={selectedScene}
                  onClose={() => onSelectScene(null)}
                  accessToken={
                    (state.status === "authenticated" ||
                      state.status === "offline") &&
                    state.session
                      ? state.session.access_token
                      : null
                  }
                  currentUserId={
                    (state.status === "authenticated" ||
                      state.status === "offline") &&
                    state.user
                      ? state.user.id
                      : ""
                  }
                />
              ) : sceneView ? (
                <ScenesOverview
                  scenes={scenes}
                  onSelectScene={onSelectScene}
                  showDraft={draftScene}
                  onDraftDismiss={() => setDraftScene(false)}
                  currentUserId={
                    (state.status === "authenticated" ||
                      state.status === "offline") &&
                    state.user
                      ? state.user.id
                      : ""
                  }
                />
              ) : selectedNote ? (
                <ErrorBoundary inline label="notepad">
                  <NotepadView
                    note={selectedNote}
                    onClose={() => onSelectNote(null)}
                  />
                </ErrorBoundary>
              ) : notesView ? (
                <NotesOverview
                  notes={notes}
                  onSelectNote={onSelectNote}
                  showDraft={draftNote}
                  onDraftDismiss={() => setDraftNote(false)}
                />
              ) : selectedGroup ? (
                <ErrorBoundary inline label="tree">
                  <TreeView
                    group={selectedGroup}
                    revealItemId={revealItemId}
                    onRevealed={clearReveal}
                  />
                </ErrorBoundary>
              ) : (
                <section className="flex items-center justify-center p-8 text-sm text-slate-500 dark:text-slate-400">
                  Select a group or note on the left, or create a new one to begin.
                </section>
              )}
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 overflow-hidden">
              <ErrorBoundary inline label="gantt">
                <Gantt
                  groups={groups}
                  focusMode={focusMode}
                  onFocusMode={setFocusMode}
                />
              </ErrorBoundary>
            </div>
          )}

          {/* Detail pane — sits to the right of either view, opens when an
              item is selected. */}
          <ErrorBoundary inline label="detail">
            <ItemDetail groups={groups} />
          </ErrorBoundary>
        </div>
        {deepLinkError && (
          <div
            role="status"
            className="pointer-events-auto fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm text-amber-900 dark:text-amber-200 shadow-lg sm:inset-x-auto sm:right-4"
          >
            <div className="flex items-start gap-2">
              <span aria-hidden>⚠️</span>
              <span className="flex-1">{deepLinkError}</span>
              <button
                type="button"
                onClick={() => setDeepLinkError(null)}
                aria-label="Dismiss"
                className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </SelectionProvider>
    </UndoProvider>
  );
}

// ---------------------------------------------------------------------------
// Paperclip header button.  Opens the cross-device clipboard popover.
// Rendered as a thin wrapper component so we can collocate the open-state
// and the relative-positioned anchor without polluting the main Home body.
// ---------------------------------------------------------------------------

function ClipboardButton({ accessToken }: { accessToken: string | null }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  if (!accessToken) return null;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Clipboard — paste from this device, copy from your other devices"
        aria-label="Clipboard"
        aria-expanded={open}
        className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
      >
        <span aria-hidden className="text-sm">📎</span>
      </button>
      {open && (
        <ClipboardPopover
          accessToken={accessToken}
          anchorRef={btnRef}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// F keyboard shortcut — toggles the "currently working on" ★ on the
// currently-selected task.  Single listener, lives inside SelectionProvider
// so it sees the shared selection used by both Tree and Gantt.  Skipped
// when typing into a field or holding a modifier (Cmd-F / Ctrl-F still
// trigger the browser's Find).
// ---------------------------------------------------------------------------

function FocusHotkey() {
  const rep = useReplicache();
  const { selectedId } = useSelection();

  useEffect(() => {
    if (!rep || !selectedId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "f" && e.key !== "F") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (target?.isContentEditable) return;
      e.preventDefault();
      // Read the item lazily inside Replicache so we always toggle the
      // *current* value — avoids stale `isFocused` from a captured items
      // snapshot if the user toggled it elsewhere milliseconds earlier.
      void rep.query(async (tx) => {
        const it = (await tx.get(`${ITEM_PREFIX}${selectedId}`)) as
          | Item
          | undefined;
        if (!it || it.type !== "task") return;
        await rep.mutate.updateItem({
          id: selectedId,
          isFocused: !it.isFocused,
        });
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rep, selectedId]);

  return null;
}

// ---------------------------------------------------------------------------
// Deep-link handler — reads `?item=<id>` from the URL on entry (e.g. from
// a notification "View task →" link), looks the item up in Replicache,
// switches the sidebar to its group, and opens the detail pane.  The
// query param is stripped immediately so a manual refresh doesn't loop
// the navigation and so the URL stays clean.
//
// Lives inside SelectionProvider because `useSelection().open()` only
// works in that subtree.  `setSelectedGroupId` lives in Home (outside
// the provider) so it's passed in.
// ---------------------------------------------------------------------------

function ItemDeepLink({
  onResolveGroup,
  onReveal,
  onForceTreeView,
  onExitFocusMode,
  onNotFound,
}: {
  onResolveGroup: (groupId: string) => void;
  onReveal: (itemId: string) => void;
  onForceTreeView: () => void;
  onExitFocusMode: () => void;
  onNotFound: () => void;
}) {
  const rep = useReplicache();
  const { open } = useSelection();
  const [params, setParams] = useSearchParams();
  const itemParam = params.get("item");

  useEffect(() => {
    if (!rep || !itemParam) return;
    let cancelled = false;
    void (async () => {
      const it = (await rep.query(async (tx) =>
        tx.get(`${ITEM_PREFIX}${itemParam}`),
      )) as Item | undefined;
      if (cancelled) return;
      // Strip the param either way — silently ignoring an unknown id is
      // better than leaving a stale ?item= around that retries on every
      // back-button hit.
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("item");
          return next;
        },
        { replace: true },
      );
      if (!it) {
        onNotFound();
        return;
      }
      // Snap to a state where the row can actually be revealed: tree
      // view (Gantt has no expandable rows), focus mode off (the row
      // may not be starred and would otherwise be filtered out), and
      // the owning group selected in the sidebar.
      onForceTreeView();
      onExitFocusMode();
      onResolveGroup(it.groupId);
      open(itemParam);
      onReveal(itemParam);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    rep,
    itemParam,
    open,
    onResolveGroup,
    onReveal,
    onForceTreeView,
    onExitFocusMode,
    onNotFound,
    setParams,
  ]);

  return null;
}

// ---------------------------------------------------------------------------
// Focus-mode tree stack — multiple per-group TreeViews vertically stacked.
// Empty state has its own copy so the user knows nothing's starred yet.
// ---------------------------------------------------------------------------

function FocusTreeStack({
  groups,
  onTurnOff,
  revealItemId,
  onRevealed,
}: {
  groups: Group[];
  onTurnOff: () => void;
  revealItemId: string | null;
  onRevealed: () => void;
}) {
  if (groups.length === 0) {
    return (
      <section className="flex flex-1 items-center justify-center p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        <div className="max-w-sm">
          <div className="text-2xl text-amber-500">★</div>
          <p className="mt-2 font-medium text-slate-700 dark:text-slate-200">
            Focus mode is on, but nothing is starred.
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Open a task and tap{" "}
            <span className="text-amber-500">★ Currently working on</span>,
            or select a task in the tree and press{" "}
            <kbd className="rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 px-1">F</kbd>.
          </p>
          <button
            type="button"
            onClick={onTurnOff}
            className="mt-3 text-xs text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Turn off focus mode
          </button>
        </div>
      </section>
    );
  }
  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {groups.map((g) => (
        <TreeView
          key={g.id}
          group={g}
          focusMode
          revealItemId={revealItemId}
          onRevealed={onRevealed}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View toggle
// ---------------------------------------------------------------------------

function ViewToggle({
  view,
  onView,
}: {
  view: ViewMode;
  onView: (v: ViewMode) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-300 dark:border-slate-600 text-sm">
      <button
        onClick={() => onView("tree")}
        className={`px-3 py-1 ${
          view === "tree"
            ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
            : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        }`}
      >
        Tree
      </button>
      <button
        onClick={() => onView("gantt")}
        className={`px-3 py-1 ${
          view === "gantt"
            ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
            : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        }`}
      >
        Gantt
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes overview — shown in the main pane when the Notes button is active.
// ---------------------------------------------------------------------------

function NotesOverview({
  notes,
  onSelectNote,
  showDraft,
  onDraftDismiss,
}: {
  notes: Note[];
  onSelectNote: (id: string) => void;
  showDraft: boolean;
  onDraftDismiss: () => void;
}) {
  const rep = useReplicache();
  const [drafting, setDrafting] = useState(false);
  const [draftName, setDraftName] = useState("");
  const draftInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showDraft) {
      setDrafting(true);
      onDraftDismiss();
    }
  }, [showDraft, onDraftDismiss]);

  useEffect(() => {
    if (drafting) {
      requestAnimationFrame(() => draftInputRef.current?.focus());
    }
  }, [drafting]);

  async function deleteNote(id: string, name: string) {
    if (!rep) return;
    if (!confirm(`Delete note "${name || "Untitled"}"? This cannot be undone.`)) return;
    await rep.mutate.deleteNote({ id });
  }

  async function commitDraft() {
    if (!rep || !draftName.trim()) {
      discardDraft();
      return;
    }
    const last = notes[notes.length - 1]?.sortKey ?? null;
    const id = uuidv7();
    await rep.mutate.createNote({
      id,
      name: draftName.trim(),
      sortKey: generateKeyBetween(last, null),
    });
    setDraftName("");
    setDrafting(false);
  }

  function discardDraft() {
    setDraftName("");
    setDrafting(false);
  }

  return (
    <section className="flex flex-col p-6">
      <header className="mb-4 flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight">Notes</h2>
        <button
          type="button"
          onClick={() => setDrafting(true)}
          className="rounded-md bg-slate-900 dark:bg-slate-100 px-2.5 py-1 text-xs font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-300"
        >
          + Note
        </button>
      </header>
      <ul className="space-y-1">
        {notes.map((n) => (
          <NoteRow
            key={n.id}
            note={n}
            onSelect={() => onSelectNote(n.id)}
            onDelete={() => void deleteNote(n.id, n.name)}
          />
        ))}
        {drafting && (
          <li className="flex items-center gap-2 rounded-md px-3 py-1.5">
            <span aria-hidden className="text-base leading-none">📝</span>
            <input
              ref={draftInputRef}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Note title…"
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void commitDraft(); }
                if (e.key === "Escape") { e.preventDefault(); discardDraft(); }
              }}
              onBlur={() => void commitDraft()}
              className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </li>
        )}
        {notes.length === 0 && !drafting && (
          <li className="px-3 py-2 text-sm italic text-slate-400 dark:text-slate-500">
            No notes yet — click + Note to create one.
          </li>
        )}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Note row — swipe-left-to-delete on touch + hover 🗑️ on desktop
// ---------------------------------------------------------------------------

const NOTE_SWIPE_COMMIT_PX = 80;
const NOTE_SWIPE_MAX_PX = 110;
const NOTE_RED_DELAY_MS = 100;
const NOTE_RED_FADE_MS = 200;

function NoteRow({
  note,
  onSelect,
  onDelete,
}: {
  note: Note;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const [swipeDx, setSwipeDx] = useState(0);
  const [redLevel, setRedLevel] = useState(0);
  const swipeDxRef = useRef(0);
  const holdRedStart = useRef<number | null>(null);
  const redRaf = useRef<number | null>(null);
  const liRef = useRef<HTMLLIElement>(null);
  const suppressClickRef = useRef(false);
  const onDeleteRef = useRef(onDelete);
  useEffect(() => { onDeleteRef.current = onDelete; }, [onDelete]);

  const stopRedAnim = useCallback(() => {
    if (redRaf.current !== null) cancelAnimationFrame(redRaf.current);
    redRaf.current = null;
    holdRedStart.current = null;
  }, []);

  const resetSwipe = useCallback(() => {
    stopRedAnim();
    swipeDxRef.current = 0;
    setSwipeDx(0);
    setRedLevel(0);
  }, [stopRedAnim]);

  useEffect(() => {
    const el = liRef.current;
    if (!el) return;

    let touchOrigin: { x: number; y: number } | null = null;
    let armed = false;
    let started = false;

    const startHoldRedTick = () => {
      holdRedStart.current = performance.now();
      const tick = (now: number) => {
        if (holdRedStart.current === null) return;
        const elapsed = now - holdRedStart.current;
        if (elapsed < NOTE_RED_DELAY_MS) {
          setRedLevel(0);
        } else {
          const t = Math.min(1, (elapsed - NOTE_RED_DELAY_MS) / NOTE_RED_FADE_MS);
          setRedLevel(t);
          if (t >= 1) {
            try { navigator.vibrate?.([60, 30, 80]); } catch { /* ignore */ }
            suppressClickRef.current = true;
            resetSwipe();
            armed = false;
            started = false;
            touchOrigin = null;
            onDeleteRef.current();
            return;
          }
        }
        redRaf.current = requestAnimationFrame(tick);
      };
      redRaf.current = requestAnimationFrame(tick);
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      touchOrigin = { x: t.clientX, y: t.clientY };
      armed = true;
      started = false;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed || !touchOrigin) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - touchOrigin.x;
      const dy = t.clientY - touchOrigin.y;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (!started) {
        if (ady > 8 && ady > adx) { armed = false; return; }
        if (adx > 6 && adx > ady) { started = true; }
      }
      if (started) {
        if (e.cancelable) e.preventDefault();
        // Left-only swipe — right is clamped to 0
        const clamped = dx > 0 ? 0 : Math.max(dx, -NOTE_SWIPE_MAX_PX);
        swipeDxRef.current = clamped;
        setSwipeDx(clamped);
        const inDeleteZone = clamped <= -NOTE_SWIPE_COMMIT_PX;
        if (inDeleteZone) {
          if (holdRedStart.current === null) startHoldRedTick();
        } else {
          if (holdRedStart.current !== null) { stopRedAnim(); setRedLevel(0); }
        }
      }
    };

    const finish = () => {
      if (started) {
        suppressClickRef.current = true;
        resetSwipe();
      }
      armed = false;
      started = false;
      touchOrigin = null;
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", finish, { passive: true });
    el.addEventListener("touchcancel", finish, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", finish);
      el.removeEventListener("touchcancel", finish);
    };
  }, [resetSwipe, stopRedAnim]);

  // amber-100 (#fef3c7) → rose-500 (#f43f5e) as redLevel goes 0→1
  let bgStyle: string | undefined;
  if (swipeDx < 0) {
    if (swipeDx <= -NOTE_SWIPE_COMMIT_PX) {
      const r = Math.round(254 + (244 - 254) * redLevel);
      const g = Math.round(243 + (63 - 243) * redLevel);
      const b = Math.round(199 + (94 - 199) * redLevel);
      bgStyle = `rgb(${r},${g},${b})`;
    } else {
      bgStyle = "rgb(241,245,249)"; // slate-100
    }
  }

  return (
    <li
      ref={liRef}
      className="group/noterow flex items-center rounded-md"
      style={{
        touchAction: "pan-y",
        transform: swipeDx ? `translateX(${swipeDx}px)` : undefined,
        transition: swipeDx === 0 ? "transform 120ms ease-out" : undefined,
        backgroundColor: bgStyle,
      }}
    >
      <button
        type="button"
        onClick={() => {
          if (suppressClickRef.current) { suppressClickRef.current = false; return; }
          onSelect();
        }}
        className="flex flex-1 items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <span aria-hidden className="text-base leading-none">📝</span>
        <span className="truncate">{note.name || "Untitled note"}</span>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        aria-label="Delete note"
        title="Delete note"
        className="mr-1 rounded px-1 text-sm leading-none text-slate-400 dark:text-slate-500 hover:bg-rose-100 dark:hover:bg-rose-900 hover:text-rose-700 dark:hover:text-rose-300 pointer-fine:invisible pointer-fine:group-hover/noterow:visible"
      >
        🗑️
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Canvas overview — shown in the main pane when the Canvas button is active.
// ---------------------------------------------------------------------------

function CanvasOverview({
  canvases,
  onSelectCanvas,
  showDraft,
  onDraftDismiss,
}: {
  canvases: Canvas[];
  onSelectCanvas: (id: string) => void;
  showDraft: boolean;
  onDraftDismiss: () => void;
}) {
  const rep = useReplicache();
  const [drafting, setDrafting] = useState(false);
  const [draftName, setDraftName] = useState("");
  const draftInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showDraft) {
      setDrafting(true);
      onDraftDismiss();
    }
  }, [showDraft, onDraftDismiss]);

  useEffect(() => {
    if (drafting) {
      requestAnimationFrame(() => draftInputRef.current?.focus());
    }
  }, [drafting]);

  async function deleteCanvas(id: string, name: string) {
    if (!rep) return;
    if (!confirm(`Delete canvas "${name || "Untitled"}"? This cannot be undone.`)) return;
    await rep.mutate.deleteCanvas({ id });
  }

  async function commitDraft() {
    if (!rep || !draftName.trim()) {
      discardDraft();
      return;
    }
    const last = canvases[canvases.length - 1]?.sortKey ?? null;
    const id = uuidv7();
    await rep.mutate.createCanvas({
      id,
      name: draftName.trim(),
      sortKey: generateKeyBetween(last, null),
    });
    setDraftName("");
    setDrafting(false);
  }

  function discardDraft() {
    setDraftName("");
    setDrafting(false);
  }

  return (
    <section className="flex flex-col p-6">
      <header className="mb-4 flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight">Canvas</h2>
        <button
          type="button"
          onClick={() => setDrafting(true)}
          className="rounded-md bg-slate-900 dark:bg-slate-100 px-2.5 py-1 text-xs font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-300"
        >
          + Canvas
        </button>
      </header>
      <ul className="space-y-1">
        {canvases.map((c) => (
          <CanvasRow
            key={c.id}
            canvas={c}
            onSelect={() => onSelectCanvas(c.id)}
            onDelete={() => void deleteCanvas(c.id, c.name)}
          />
        ))}
        {drafting && (
          <li className="flex items-center gap-2 rounded-md px-3 py-1.5">
            <span aria-hidden className="text-base leading-none">🎨</span>
            <input
              ref={draftInputRef}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Canvas name…"
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void commitDraft(); }
                if (e.key === "Escape") { e.preventDefault(); discardDraft(); }
              }}
              onBlur={() => void commitDraft()}
              className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </li>
        )}
        {canvases.length === 0 && !drafting && (
          <li className="px-3 py-2 text-sm italic text-slate-400 dark:text-slate-500">
            No canvases yet — click + Canvas to create one.
          </li>
        )}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Canvas row — swipe-left-to-delete on touch + hover 🗑️ on desktop
// (shares the same swipe mechanics as NoteRow)
// ---------------------------------------------------------------------------

const CANVAS_SWIPE_COMMIT_PX = 80;
const CANVAS_SWIPE_MAX_PX = 110;
const CANVAS_RED_DELAY_MS = 100;
const CANVAS_RED_FADE_MS = 200;

function CanvasRow({
  canvas,
  onSelect,
  onDelete,
}: {
  canvas: Canvas;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const [swipeDx, setSwipeDx] = useState(0);
  const [redLevel, setRedLevel] = useState(0);
  const swipeDxRef = useRef(0);
  const holdRedStart = useRef<number | null>(null);
  const redRaf = useRef<number | null>(null);
  const liRef = useRef<HTMLLIElement>(null);
  const suppressClickRef = useRef(false);
  const onDeleteRef = useRef(onDelete);
  useEffect(() => { onDeleteRef.current = onDelete; }, [onDelete]);

  const stopRedAnim = useCallback(() => {
    if (redRaf.current !== null) cancelAnimationFrame(redRaf.current);
    redRaf.current = null;
    holdRedStart.current = null;
  }, []);

  const resetSwipe = useCallback(() => {
    stopRedAnim();
    swipeDxRef.current = 0;
    setSwipeDx(0);
    setRedLevel(0);
  }, [stopRedAnim]);

  useEffect(() => {
    const el = liRef.current;
    if (!el) return;

    let touchOrigin: { x: number; y: number } | null = null;
    let armed = false;
    let started = false;

    const startHoldRedTick = () => {
      holdRedStart.current = performance.now();
      const tick = (now: number) => {
        if (holdRedStart.current === null) return;
        const elapsed = now - holdRedStart.current;
        if (elapsed < CANVAS_RED_DELAY_MS) {
          setRedLevel(0);
        } else {
          const t = Math.min(1, (elapsed - CANVAS_RED_DELAY_MS) / CANVAS_RED_FADE_MS);
          setRedLevel(t);
          if (t >= 1) {
            try { navigator.vibrate?.([60, 30, 80]); } catch { /* ignore */ }
            suppressClickRef.current = true;
            resetSwipe();
            armed = false;
            started = false;
            touchOrigin = null;
            onDeleteRef.current();
            return;
          }
        }
        redRaf.current = requestAnimationFrame(tick);
      };
      redRaf.current = requestAnimationFrame(tick);
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      touchOrigin = { x: t.clientX, y: t.clientY };
      armed = true;
      started = false;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed || !touchOrigin) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - touchOrigin.x;
      const dy = t.clientY - touchOrigin.y;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      if (!started) {
        if (ady > 8 && ady > adx) { armed = false; return; }
        if (adx > 6 && adx > ady) { started = true; }
      }
      if (started) {
        if (e.cancelable) e.preventDefault();
        const clamped = dx > 0 ? 0 : Math.max(dx, -CANVAS_SWIPE_MAX_PX);
        swipeDxRef.current = clamped;
        setSwipeDx(clamped);
        const inDeleteZone = clamped <= -CANVAS_SWIPE_COMMIT_PX;
        if (inDeleteZone) {
          if (holdRedStart.current === null) startHoldRedTick();
        } else {
          if (holdRedStart.current !== null) { stopRedAnim(); setRedLevel(0); }
        }
      }
    };

    const finish = () => {
      if (started) {
        suppressClickRef.current = true;
        resetSwipe();
      }
      armed = false;
      started = false;
      touchOrigin = null;
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", finish, { passive: true });
    el.addEventListener("touchcancel", finish, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", finish);
      el.removeEventListener("touchcancel", finish);
    };
  }, [resetSwipe, stopRedAnim]);

  let bgStyle: string | undefined;
  if (swipeDx < 0) {
    if (swipeDx <= -CANVAS_SWIPE_COMMIT_PX) {
      const r = Math.round(254 + (244 - 254) * redLevel);
      const g = Math.round(243 + (63 - 243) * redLevel);
      const b = Math.round(199 + (94 - 199) * redLevel);
      bgStyle = `rgb(${r},${g},${b})`;
    } else {
      bgStyle = "rgb(241,245,249)";
    }
  }

  return (
    <li
      ref={liRef}
      className="group/canvasrow flex items-center rounded-md"
      style={{
        touchAction: "pan-y",
        transform: swipeDx ? `translateX(${swipeDx}px)` : undefined,
        transition: swipeDx === 0 ? "transform 120ms ease-out" : undefined,
        backgroundColor: bgStyle,
      }}
    >
      <button
        type="button"
        onClick={() => {
          if (suppressClickRef.current) { suppressClickRef.current = false; return; }
          onSelect();
        }}
        className="flex flex-1 items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <span aria-hidden className="text-base leading-none">🎨</span>
        <span className="truncate">{canvas.name || "Untitled canvas"}</span>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        aria-label="Delete canvas"
        title="Delete canvas"
        className="mr-1 rounded px-1 text-sm leading-none text-slate-400 dark:text-slate-500 hover:bg-rose-100 dark:hover:bg-rose-900 hover:text-rose-700 dark:hover:text-rose-300 pointer-fine:invisible pointer-fine:group-hover/canvasrow:visible"
      >
        🗑️
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Excalidraw overview — shown in the main pane when the Excalidraw button is
// active.  Mirrors CanvasOverview.
// ---------------------------------------------------------------------------

function ScenesOverview({
  scenes,
  onSelectScene,
  showDraft,
  onDraftDismiss,
  currentUserId,
}: {
  scenes: Scene[];
  onSelectScene: (id: string) => void;
  showDraft: boolean;
  onDraftDismiss: () => void;
  currentUserId: string;
}) {
  const rep = useReplicache();
  const [drafting, setDrafting] = useState(false);
  const [draftName, setDraftName] = useState("");
  const draftInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showDraft) {
      setDrafting(true);
      onDraftDismiss();
    }
  }, [showDraft, onDraftDismiss]);

  useEffect(() => {
    if (drafting) {
      requestAnimationFrame(() => draftInputRef.current?.focus());
    }
  }, [drafting]);

  async function deleteScene(id: string, name: string) {
    if (!rep) return;
    if (!confirm(`Delete drawing "${name || "Untitled"}"? This cannot be undone.`))
      return;
    await rep.mutate.deleteScene({ id });
  }

  async function commitDraft() {
    if (!rep || !draftName.trim()) {
      discardDraft();
      return;
    }
    const last = scenes[scenes.length - 1]?.sortKey ?? null;
    const id = uuidv7();
    await rep.mutate.createScene({
      id,
      name: draftName.trim(),
      sortKey: generateKeyBetween(last, null),
    });
    setDraftName("");
    setDrafting(false);
  }

  function discardDraft() {
    setDraftName("");
    setDrafting(false);
  }

  return (
    <section className="flex flex-col p-6">
      <header className="mb-4 flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight">Excalidraw</h2>
        <button
          type="button"
          onClick={() => setDrafting(true)}
          className="rounded-md bg-slate-900 dark:bg-slate-100 px-2.5 py-1 text-xs font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-200"
        >
          + Drawing
        </button>
      </header>
      {scenes.length === 0 && !drafting ? (
        <p className="px-1 py-2 text-sm italic text-slate-400 dark:text-slate-500">
          No drawings yet — click + Drawing to create one.
        </p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,200px)] justify-center gap-x-4 gap-y-5 sm:justify-start">
          {drafting && (
            <div className="flex w-[200px] flex-col">
              <div className="flex h-[200px] w-[200px] items-center justify-center rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900">
                <img
                  src="/excalidraw-icon.svg"
                  alt=""
                  aria-hidden
                  className="h-12 w-12 opacity-50"
                />
              </div>
              <input
                ref={draftInputRef}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="Drawing name…"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void commitDraft(); }
                  if (e.key === "Escape") { e.preventDefault(); discardDraft(); }
                }}
                onBlur={() => void commitDraft()}
                className="mt-1.5 w-[200px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-sm text-slate-900 dark:text-slate-100 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              />
            </div>
          )}
          {scenes.map((s) => (
            <SceneCard
              key={s.id}
              scene={s}
              shared={!!s.ownerId && currentUserId !== "" && s.ownerId !== currentUserId}
              onSelect={() => onSelectScene(s.id)}
              onDelete={() => void deleteScene(s.id, s.name)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Scene thumbnail — renders the drawing's contents to an SVG preview via
// Excalidraw's exportToSvg.  That util lives in the heavy (~1MB)
// @excalidraw/excalidraw bundle, so we import it on demand and memoise the
// generated data-URL per scene (keyed by the stored blob) in a module-level
// cache, so switching views doesn't re-render every thumbnail.  Embedded image
// elements are drawn as placeholders here — we don't fetch their bytes (which
// live in MinIO) just for the overview.
// ---------------------------------------------------------------------------

type ExportToSvg = (typeof import("@excalidraw/excalidraw"))["exportToSvg"];

let exportToSvgPromise: Promise<ExportToSvg> | null = null;
function loadExportToSvg(): Promise<ExportToSvg> {
  if (!exportToSvgPromise) {
    exportToSvgPromise = import("@excalidraw/excalidraw").then(
      (m) => m.exportToSvg,
    );
  }
  return exportToSvgPromise;
}

const sceneThumbCache = new Map<string, { key: string; src: string }>();

function SceneThumbnail({ scene }: { scene: Scene }) {
  const hit = sceneThumbCache.get(scene.id);
  const [src, setSrc] = useState<string | null>(
    hit && hit.key === scene.scene ? hit.src : null,
  );
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    let alive = true;
    const key = scene.scene;
    const cached = sceneThumbCache.get(scene.id);
    if (cached && cached.key === key) {
      setSrc(cached.src);
      setEmpty(false);
      return;
    }
    const { elements, appState } = parseScene(key);
    const live = elements.filter((e) => !e.isDeleted);
    if (live.length === 0) {
      setSrc(null);
      setEmpty(true);
      return;
    }
    setEmpty(false);
    void (async () => {
      try {
        const exportToSvg = await loadExportToSvg();
        const svg = await exportToSvg({
          elements: live as Parameters<ExportToSvg>[0]["elements"],
          appState: {
            viewBackgroundColor: appState.viewBackgroundColor ?? "#ffffff",
            exportBackground: true,
          },
          files: null,
          exportPadding: 8,
          skipInliningFonts: true,
        });
        const markup = new XMLSerializer().serializeToString(svg);
        const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(markup)}`;
        if (!alive) return;
        sceneThumbCache.set(scene.id, { key, src: dataUrl });
        setSrc(dataUrl);
      } catch (err) {
        console.warn("[scene] thumbnail render failed", err);
        if (alive) setEmpty(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scene.id, scene.scene]);

  if (empty) {
    return (
      <img
        src="/excalidraw-icon.svg"
        alt=""
        aria-hidden
        className="h-12 w-12 opacity-30"
      />
    );
  }
  if (!src) {
    return (
      <div className="h-full w-full animate-pulse bg-slate-100 dark:bg-slate-800" />
    );
  }
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      loading="lazy"
      className="h-full w-full object-contain"
    />
  );
}

// ---------------------------------------------------------------------------
// Scene card — a 200×200 thumbnail of the drawing with its name underneath and
// a delete button in the top-right corner (always shown on touch, hover-reveal
// on fine pointers).
// ---------------------------------------------------------------------------

function SceneCard({
  scene,
  shared,
  onSelect,
  onDelete,
}: {
  scene: Scene;
  shared: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group/scenecard flex w-[200px] flex-col">
      <div className="relative">
        <button
          type="button"
          onClick={onSelect}
          title={scene.name || "Untitled drawing"}
          className="block h-[200px] w-[200px] overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-white shadow-sm transition hover:border-slate-300 dark:hover:border-slate-600 hover:shadow"
        >
          <span className="flex h-full w-full items-center justify-center">
            <SceneThumbnail scene={scene} />
          </span>
        </button>
        {!shared && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            aria-label="Delete drawing"
            title="Delete drawing"
            className="absolute right-1.5 top-1.5 rounded-md bg-white/90 dark:bg-slate-800/90 p-1 text-sm leading-none text-slate-500 dark:text-slate-300 shadow-sm hover:bg-rose-50 dark:hover:bg-rose-950 hover:text-rose-700 dark:hover:text-rose-300 pointer-fine:invisible pointer-fine:group-hover/scenecard:visible"
          >
            🗑️
          </button>
        )}
      </div>
      <div className="mt-1.5 flex items-center justify-center gap-1 px-1">
        <span className="truncate text-sm text-slate-700 dark:text-slate-200">
          {scene.name || "Untitled drawing"}
        </span>
        {shared && (
          <span className="shrink-0 rounded bg-amber-100 dark:bg-amber-900 px-1 text-[10px] font-medium text-amber-700 dark:text-amber-300">
            shared
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groups sidebar
// ---------------------------------------------------------------------------

// Storage key for which folders the user has collapsed.  Per-device, not
// synced — collapse is an interaction preference, not a data property.
const FOLDER_COLLAPSED_KEY = "johndo:foldedFolders";

function GroupsSidebar({
  groups,
  folders,
  selectedId,
  isNotesView,
  isCanvasView,
  isSceneView,
  currentUserId,
  accessToken,
  onSelect,
  isTodayView,
  onTodayView,
  onNotesView,
  onCreateNote,
  onCanvasView,
  onCreateCanvas,
  onSceneView,
  onCreateScene,
  collapsed,
  onToggleCollapsed,
}: {
  groups: Group[];
  folders: Folder[];
  selectedId: string | null;
  isNotesView: boolean;
  isCanvasView: boolean;
  isSceneView: boolean;
  /** Used to mark groups whose ownerId !== me as "shared from someone
   *  else" in the sidebar.  May be empty during the bootstrap window. */
  currentUserId: string;
  accessToken: string | null;
  onSelect: (id: string) => void;
  isTodayView: boolean;
  onTodayView: () => void;
  onNotesView: () => void;
  onCreateNote: () => void;
  onCanvasView: () => void;
  onCreateCanvas: () => void;
  onSceneView: () => void;
  onCreateScene: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const rep = useReplicache();
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState<"group" | "folder">("group");
  const [newItemName, setNewItemName] = useState("");
  const createInputRef = useRef<HTMLInputElement>(null);
  const [shareGroupId, setShareGroupId] = useState<string | null>(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [moveMenuFor, setMoveMenuFor] = useState<string | null>(null);

  useEffect(() => {
    if (showCreate) createInputRef.current?.focus();
  }, [showCreate]);

  // Drag state.  IDs are prefixed (g:<id> / f:<id>) so groups and folders
  // share the dnd-kit ID space without collisions.  `over.pos` resolves
  // the cursor position within the drop target's rect:
  //   - dragging a group onto another group: before/after = reorder
  //   - dragging a group onto a folder header: inside = move into folder
  //   - dragging a folder onto another folder: before/after = reorder
  // Invalid combinations (group↔folder before/after, folder↔group) leave
  // `over` null and the drop is a no-op.
  type DragOverPos = "before" | "after" | "inside";
  type Drag = {
    activeId: string;
    over: { id: string; pos: DragOverPos } | null;
  };
  const [drag, setDrag] = useState<Drag | null>(null);

  // PointerSensor with a small activation distance keeps plain clicks
  // (select group / toggle folder / open menu) working — drag only kicks
  // in after 6 px of movement.  TouchSensor uses a 200 ms long-press so
  // taps still select on touchscreens without false-positive drags.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
  );

  // Per-device collapse state for folders.  Default: every folder open.
  const [foldedFolderIds, setFoldedFolderIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(FOLDER_COLLAPSED_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? new Set(parsed as string[]) : new Set();
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    localStorage.setItem(
      FOLDER_COLLAPSED_KEY,
      JSON.stringify([...foldedFolderIds]),
    );
  }, [foldedFolderIds]);

  if (collapsed) {
    return (
      <aside className="flex flex-col items-center gap-3 py-3">
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Show groups"
          aria-label="Show groups"
          className="rounded p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100"
        >
          ▸
        </button>
      </aside>
    );
  }

  // Index groups by their folder.  folderId on shared groups is the
  // RECIPIENT's preferred folder (from group_view_prefs joined into the
  // pull) — owner-side metadata never reaches the recipient — so the
  // same bucket-by-folderId works for both owned and shared rows.
  const groupsByFolder = new Map<string | null, Group[]>();
  for (const g of groups) {
    const key = g.folderId ?? null;
    const bucket = groupsByFolder.get(key);
    if (bucket) bucket.push(g);
    else groupsByFolder.set(key, [g]);
  }
  for (const bucket of groupsByFolder.values()) {
    bucket.sort((a, b) =>
      a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0,
    );
  }
  const topLevelGroups = groupsByFolder.get(null) ?? [];

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!rep || !newItemName.trim()) return;
    if (createType === "group") {
      const last = topLevelGroups[topLevelGroups.length - 1]?.sortKey ?? null;
      const id = uuidv7();
      await rep.mutate.createGroup({
        id,
        name: newItemName.trim(),
        sortKey: generateKeyBetween(last, null),
      });
      onSelect(id);
    } else {
      const last = folders[folders.length - 1]?.sortKey ?? null;
      const id = uuidv7();
      await rep.mutate.createFolder({
        id,
        name: newItemName.trim(),
        sortKey: generateKeyBetween(last, null),
      });
    }
    setNewItemName("");
    setShowCreate(false);
  }

  async function deleteFolder(f: Folder) {
    if (!rep) return;
    const inFolder = (groupsByFolder.get(f.id) ?? []).length;
    const suffix =
      inFolder > 0
        ? ` Its ${inFolder} group${inFolder === 1 ? "" : "s"} will move back to the top level.`
        : "";
    if (!confirm(`Delete the folder "${f.name}"?${suffix}`)) return;
    await rep.mutate.deleteFolder({ id: f.id });
  }

  async function deleteGroup(g: Group) {
    if (!rep) return;
    if (!confirm(`Delete the group "${g.name}" and everything in it?`)) return;
    if (
      !confirm(
        `This will permanently delete "${g.name}" and every task, time ` +
          `entry, and attachment inside it.\n\nProceed?`,
      )
    )
      return;
    await rep.mutate.deleteGroup({ id: g.id });
    if (selectedId === g.id) onSelect("");
  }

  /** Move a group into a folder (or out to top level when folderId is null).
   *  Appends to the end of the target bucket.  Routes to the right
   *  mutator based on ownership: owner-side writes the group row;
   *  recipients write their own group_view_prefs row instead. */
  async function moveGroupToFolder(group: Group, folderId: string | null) {
    if (!rep) return;
    if ((group.folderId ?? null) === folderId) {
      setMoveMenuFor(null);
      return;
    }
    const targetBucket = groupsByFolder.get(folderId) ?? [];
    const last = targetBucket[targetBucket.length - 1]?.sortKey ?? null;
    const sortKey = generateKeyBetween(last, null);
    if (isOwnedByMe(group)) {
      await rep.mutate.updateGroup({ id: group.id, folderId, sortKey });
    } else {
      await rep.mutate.setSharedGroupView({
        groupId: group.id,
        folderId,
        sortKey,
      });
    }
    setMoveMenuFor(null);
  }

  function isOwnedByMe(group: Group): boolean {
    return !group.ownerId || group.ownerId === currentUserId;
  }

  function toggleFolder(id: string) {
    setFoldedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ----- Drag handlers -----

  const parseDragId = (
    raw: string,
  ): { kind: "g" | "f"; id: string } | null => {
    if (raw.startsWith("g:")) return { kind: "g", id: raw.slice(2) };
    if (raw.startsWith("f:")) return { kind: "f", id: raw.slice(2) };
    return null;
  };

  const onDragStart = (e: DragStartEvent) => {
    setDrag({ activeId: String(e.active.id), over: null });
  };

  const onDragMove = (e: DragMoveEvent) => {
    if (!drag) return;
    if (!e.over) {
      setDrag((d) => (d && d.over !== null ? { ...d, over: null } : d));
      return;
    }
    const activeP = parseDragId(drag.activeId);
    const overP = parseDragId(String(e.over.id));
    if (!activeP || !overP) return;
    if (drag.activeId === e.over.id) {
      setDrag((d) => (d && d.over !== null ? { ...d, over: null } : d));
      return;
    }
    // Decide whether before/after/inside is valid for this combination.
    let pos: DragOverPos;
    if (activeP.kind === "g" && overP.kind === "f") {
      // Group onto folder header → only "inside" makes sense.
      pos = "inside";
    } else if (activeP.kind === "g" && overP.kind === "g") {
      // Group onto group → before/after by cursor position.
      const activator = e.activatorEvent as PointerEvent;
      const pointerY = (activator.clientY ?? 0) + e.delta.y;
      const rect = e.over.rect;
      pos = pointerY < rect.top + rect.height / 2 ? "before" : "after";
    } else if (activeP.kind === "f" && overP.kind === "f") {
      // Folder onto folder → before/after by cursor position.
      const activator = e.activatorEvent as PointerEvent;
      const pointerY = (activator.clientY ?? 0) + e.delta.y;
      const rect = e.over.rect;
      pos = pointerY < rect.top + rect.height / 2 ? "before" : "after";
    } else {
      // Folder onto group (or anything else) → no valid drop.
      setDrag((d) => (d && d.over !== null ? { ...d, over: null } : d));
      return;
    }
    setDrag((d) => {
      if (!d) return d;
      if (d.over?.id === e.over!.id && d.over.pos === pos) return d;
      return { ...d, over: { id: String(e.over!.id), pos } };
    });
  };

  const onDragEnd = async (_e: DragEndEvent) => {
    const current = drag;
    setDrag(null);
    if (!current?.over || !rep) return;
    const activeP = parseDragId(current.activeId);
    const overP = parseDragId(current.over.id);
    if (!activeP || !overP) return;

    if (activeP.kind === "g" && overP.kind === "g") {
      // Reorder a group relative to another group.  The target's bucket
      // (its folder, or top level) becomes the dragged group's bucket too
      // — so this also handles cross-bucket moves "land me next to that
      // group over there".
      const active = groups.find((x) => x.id === activeP.id);
      const target = groups.find((x) => x.id === overP.id);
      if (!active || !target) return;
      const targetBucket =
        groupsByFolder.get(target.folderId ?? null) ?? [];
      // Filter the dragged item out of the bucket so the neighbours we
      // compute are the post-move neighbours (matters when reordering
      // within the same bucket — otherwise generateKeyBetween could pick
      // the same slot the dragged item used to occupy).
      const bucketWithoutActive = targetBucket.filter(
        (g) => g.id !== activeP.id,
      );
      const newIdx = bucketWithoutActive.findIndex(
        (g) => g.id === target.id,
      );
      const insertIdx = current.over.pos === "before" ? newIdx : newIdx + 1;
      const before = bucketWithoutActive[insertIdx - 1] ?? null;
      const after = bucketWithoutActive[insertIdx] ?? null;
      const sortKey = generateKeyBetween(
        before?.sortKey ?? null,
        after?.sortKey ?? null,
      );
      const newFolderId = target.folderId ?? null;
      if (isOwnedByMe(active)) {
        await rep.mutate.updateGroup({
          id: activeP.id,
          folderId: newFolderId,
          sortKey,
        });
      } else {
        await rep.mutate.setSharedGroupView({
          groupId: activeP.id,
          folderId: newFolderId,
          sortKey,
        });
      }
      return;
    }

    if (activeP.kind === "g" && overP.kind === "f") {
      // Drop a group into a folder — append to end.
      const active = groups.find((x) => x.id === activeP.id);
      if (!active) return;
      const targetBucket = groupsByFolder.get(overP.id) ?? [];
      const last = targetBucket[targetBucket.length - 1]?.sortKey ?? null;
      const sortKey = generateKeyBetween(last, null);
      if (isOwnedByMe(active)) {
        await rep.mutate.updateGroup({
          id: activeP.id,
          folderId: overP.id,
          sortKey,
        });
      } else {
        await rep.mutate.setSharedGroupView({
          groupId: activeP.id,
          folderId: overP.id,
          sortKey,
        });
      }
      return;
    }

    if (activeP.kind === "f" && overP.kind === "f") {
      // Reorder folders among each other.
      const target = folders.find((x) => x.id === overP.id);
      if (!target) return;
      const foldersWithoutActive = folders.filter(
        (f) => f.id !== activeP.id,
      );
      const newIdx = foldersWithoutActive.findIndex(
        (f) => f.id === target.id,
      );
      const insertIdx = current.over.pos === "before" ? newIdx : newIdx + 1;
      const before = foldersWithoutActive[insertIdx - 1] ?? null;
      const after = foldersWithoutActive[insertIdx] ?? null;
      const sortKey = generateKeyBetween(
        before?.sortKey ?? null,
        after?.sortKey ?? null,
      );
      await rep.mutate.updateFolder({ id: activeP.id, sortKey });
      return;
    }
  };

  const onDragCancel = () => {
    setDrag(null);
  };

  // Folders auto-expand when a group is being dragged so a collapsed
  // folder doesn't hide its drop target.
  const dragActiveKind = drag ? parseDragId(drag.activeId)?.kind : undefined;
  const dragForceExpandFolders =
    drag !== null && dragActiveKind === "g";

  const shareGroup = shareGroupId
    ? groups.find((g) => g.id === shareGroupId) ?? null
    : null;

  return (
    <aside className="flex flex-col gap-3 p-4">
      <div className="group/hdr flex items-center justify-between">
        <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Groups
        </h2>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            title="New group or folder"
            className="rounded p-1 text-slate-400 dark:text-slate-500 opacity-30 transition-opacity group-hover/hdr:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300"
          >
            +
          </button>
          <button
            type="button"
            onClick={onToggleCollapsed}
            title="Hide sidebar"
            aria-label="Hide sidebar"
            className="rounded p-1 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
          >
            ◂
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="flex flex-col gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2">
          <div className="flex overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs">
            <button
              type="button"
              onClick={() => { setCreateType("group"); createInputRef.current?.focus(); }}
              className={`flex-1 px-3 py-1.5 font-medium transition-colors ${
                createType === "group"
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              Group
            </button>
            <button
              type="button"
              onClick={() => { setCreateType("folder"); createInputRef.current?.focus(); }}
              className={`flex-1 px-3 py-1.5 font-medium transition-colors ${
                createType === "folder"
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              Folder
            </button>
          </div>
          <form onSubmit={(e) => { void handleCreate(e); }} className="flex gap-1">
            <input
              ref={createInputRef}
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder={createType === "group" ? "Group name…" : "Folder name…"}
              onKeyDown={(e) => { if (e.key === "Escape") { setShowCreate(false); setNewItemName(""); } }}
              className="flex-1 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-xs focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
            <button
              type="submit"
              disabled={!newItemName.trim()}
              className="rounded-md bg-slate-900 dark:bg-slate-100 px-2 text-xs font-medium text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-slate-300 disabled:opacity-50"
            >
              Add
            </button>
          </form>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <ul className="space-y-1">
          {folders.map((f) => {
            const inside = groupsByFolder.get(f.id) ?? [];
            const isFolded =
              !dragForceExpandFolders && foldedFolderIds.has(f.id);
            const dragDropIndicator =
              drag?.over?.id === `f:${f.id}` ? drag.over.pos : null;
            return (
              <li key={f.id} className="group">
                <FolderHeader
                  folder={f}
                  folded={isFolded}
                  renaming={renamingFolderId === f.id}
                  onToggle={() => toggleFolder(f.id)}
                  onBeginRename={() => setRenamingFolderId(f.id)}
                  onEndRename={async (name) => {
                    setRenamingFolderId(null);
                    if (rep && name && name !== f.name) {
                      await rep.mutate.updateFolder({ id: f.id, name });
                    }
                  }}
                  onCancelRename={() => setRenamingFolderId(null)}
                  onDelete={() => void deleteFolder(f)}
                  dropIndicator={dragDropIndicator}
                  isDragging={drag?.activeId === `f:${f.id}`}
                />
                {!isFolded && inside.length > 0 && (
                  <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-slate-200 dark:border-slate-700 pl-2">
                    {inside.map((g) => (
                      <GroupRow
                        key={g.id}
                        group={g}
                        currentUserId={currentUserId}
                        selected={selectedId === g.id}
                        onSelect={() => onSelect(g.id)}
                        onShare={() => setShareGroupId(g.id)}
                        onDelete={() => void deleteGroup(g)}
                        onOpenMoveMenu={() => setMoveMenuFor(g.id)}
                        moveMenuOpen={moveMenuFor === g.id}
                        onCloseMoveMenu={() => setMoveMenuFor(null)}
                        folders={folders}
                        onMoveToFolder={(folderId) =>
                          void moveGroupToFolder(g, folderId)
                        }
                        dropIndicator={
                          drag?.over?.id === `g:${g.id}` &&
                          drag.over.pos !== "inside"
                            ? drag.over.pos
                            : null
                        }
                        isDragging={drag?.activeId === `g:${g.id}`}
                      />
                    ))}
                  </ul>
                )}
                {!isFolded && inside.length === 0 && (
                  <p className="ml-5 mt-0.5 text-[11px] italic text-slate-400 dark:text-slate-500">
                    Empty — drag a group here.
                  </p>
                )}
              </li>
            );
          })}

          {topLevelGroups.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              currentUserId={currentUserId}
              selected={selectedId === g.id}
              onSelect={() => onSelect(g.id)}
              onShare={() => setShareGroupId(g.id)}
              onDelete={() => void deleteGroup(g)}
              onOpenMoveMenu={() => setMoveMenuFor(g.id)}
              moveMenuOpen={moveMenuFor === g.id}
              onCloseMoveMenu={() => setMoveMenuFor(null)}
              folders={folders}
              onMoveToFolder={(folderId) =>
                void moveGroupToFolder(g, folderId)
              }
              dropIndicator={
                drag?.over?.id === `g:${g.id}` &&
                drag.over.pos !== "inside"
                  ? drag.over.pos
                  : null
              }
              isDragging={drag?.activeId === `g:${g.id}`}
            />
          ))}

          {folders.length === 0 && groups.length === 0 && (
            <li className="px-2 py-1 text-xs text-slate-400 dark:text-slate-500">No groups yet.</li>
          )}
        </ul>
      </DndContext>

      <div className="border-t border-slate-200 dark:border-slate-700 pt-2">
        <button
          type="button"
          onClick={onTodayView}
          className={`mb-0.5 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium ${
            isTodayView
              ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
          }`}
        >
          <span aria-hidden className="text-base leading-none">☀️</span>
          Today
        </button>
        <div className="group/notesbtn flex items-center gap-0.5">
          <button
            type="button"
            onClick={onNotesView}
            className={`flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium ${
              isNotesView
                ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <span aria-hidden className="text-base leading-none">📝</span>
            Notes
          </button>
          <button
            type="button"
            onClick={onCreateNote}
            title="New note"
            className="rounded p-1 text-slate-400 dark:text-slate-500 opacity-30 transition-opacity group-hover/notesbtn:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300"
          >
            +
          </button>
        </div>
        <div className="group/canvasbtn flex items-center gap-0.5">
          <button
            type="button"
            onClick={onCanvasView}
            className={`flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium ${
              isCanvasView
                ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <span aria-hidden className="text-base leading-none">🎨</span>
            Canvas
          </button>
          <button
            type="button"
            onClick={onCreateCanvas}
            title="New canvas"
            className="rounded p-1 text-slate-400 dark:text-slate-500 opacity-30 transition-opacity group-hover/canvasbtn:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300"
          >
            +
          </button>
        </div>
        <div className="group/scenebtn flex items-center gap-0.5">
          <button
            type="button"
            onClick={onSceneView}
            className={`flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium ${
              isSceneView
                ? "bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <img
              src="/excalidraw-icon.svg"
              alt=""
              aria-hidden
              className="h-[18px] w-[18px] shrink-0"
            />
            Excalidraw
          </button>
          <button
            type="button"
            onClick={onCreateScene}
            title="New drawing"
            className="rounded p-1 text-slate-400 dark:text-slate-500 opacity-30 transition-opacity group-hover/scenebtn:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300"
          >
            +
          </button>
        </div>
      </div>

      {selectedId && accessToken && (
        <GroupSharingPanel
          group={groups.find((g) => g.id === selectedId) ?? null}
          currentUserId={currentUserId}
          accessToken={accessToken}
        />
      )}

      {shareGroup && (
        <Modal
          title={`Share "${shareGroup.name}"`}
          onClose={() => setShareGroupId(null)}
        >
          <ShareSection scope="group" scopeId={shareGroup.id} />
        </Modal>
      )}
    </aside>
  );
}

// Inline folder rename: double-click the folder header swaps the label
// for this input.  Enter commits, Esc discards, blur commits.
function FolderRenameInput({
  folder,
  onCommit,
  onCancel,
}: {
  folder: Folder;
  onCommit: (name: string) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(folder.name);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.select();
    });
  }, []);
  return (
    <input
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void onCommit(draft.trim() || folder.name)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(folder.name);
          onCancel();
        }
      }}
      className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200"
    />
  );
}

// One group row.  Draggable + droppable for reorder/move-to-folder.
// Shared-with-me groups are NOT draggable (their order/folder lives on
// the owner side) but they are valid drop targets for reordering owned
// groups against — keeps the sidebar interactions symmetric.
function GroupRow({
  group,
  currentUserId,
  selected,
  onSelect,
  onShare,
  onDelete,
  onOpenMoveMenu,
  moveMenuOpen,
  onCloseMoveMenu,
  folders,
  onMoveToFolder,
  dropIndicator,
  isDragging,
}: {
  group: Group;
  currentUserId: string;
  selected: boolean;
  onSelect: () => void;
  onShare: () => void;
  onDelete: () => void;
  onOpenMoveMenu: () => void;
  moveMenuOpen: boolean;
  onCloseMoveMenu: () => void;
  folders: Folder[];
  onMoveToFolder: (folderId: string | null) => void;
  dropIndicator: "before" | "after" | null;
  isDragging: boolean;
}) {
  const isShared =
    currentUserId !== "" &&
    !!group.ownerId &&
    group.ownerId !== currentUserId;
  // Shared groups can now be filed + reordered too — the recipient-side
  // sortKey/folderId lives in group_view_prefs, not on the group row, so
  // the owner is unaffected by the recipient rearranging things.
  const dragId = `g:${group.id}`;
  const draggable = useDraggable({ id: dragId });
  const droppable = useDroppable({ id: dragId });

  const setRef = (el: HTMLLIElement | null) => {
    draggable.setNodeRef(el);
    droppable.setNodeRef(el);
  };

  return (
    <li
      ref={setRef}
      className={`group/row relative flex items-center gap-1 rounded-md ${
        isDragging ? "opacity-40" : ""
      } ${
        dropIndicator === "before"
          ? "shadow-[inset_0_2px_0_0_#6366f1]"
          : dropIndicator === "after"
            ? "shadow-[inset_0_-2px_0_0_#6366f1]"
            : ""
      }`}
    >
      {/* Drag handle.  Pointer-events on the handle activate the drag
          listeners; the rest of the row stays clickable for select/menu.
          Shared groups get the same handle — the recipient's reorder /
          file writes to their own group_view_prefs row, not to the
          owner's group, so the owner is unaffected. */}
      <button
        type="button"
        {...draggable.listeners}
        {...draggable.attributes}
        aria-label="Drag to reorder or file"
        title="Drag to reorder or drop into a folder"
        className="cursor-grab touch-none rounded px-0.5 text-xs text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 active:cursor-grabbing pointer-fine:invisible pointer-fine:group-hover/row:visible"
      >
        ⋮⋮
      </button>
      <button
        onClick={onSelect}
        className={`flex-1 rounded-md px-2 py-1 text-left text-sm ${
          selected
            ? "bg-slate-200 dark:bg-slate-700 font-medium text-slate-900 dark:text-slate-100"
            : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
        }`}
      >
        <span className="inline-flex items-center gap-1.5">
          {group.name}
          {isShared && (
            <span
              className="rounded bg-amber-100 dark:bg-amber-900 px-1 text-[10px] font-medium text-amber-700 dark:text-amber-300"
              title="Shared with you"
            >
              shared
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={() => (moveMenuOpen ? onCloseMoveMenu() : onOpenMoveMenu())}
        className="rounded px-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 pointer-fine:invisible pointer-fine:group-hover/row:visible"
        aria-label="Move to folder"
        title="Move to folder…"
      >
        ⤴
      </button>
      <button
        type="button"
        onClick={onShare}
        className="rounded px-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 pointer-fine:invisible pointer-fine:group-hover/row:visible"
        aria-label="Share group"
        title="Share this group"
      >
        ↗
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="rounded px-1 text-xs text-slate-400 dark:text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 pointer-fine:invisible pointer-fine:group-hover/row:visible"
        aria-label="Delete group"
      >
        ✕
      </button>

      {moveMenuOpen && (
        <MoveToFolderMenu
          currentFolderId={group.folderId ?? null}
          folders={folders}
          onPick={onMoveToFolder}
          onClose={onCloseMoveMenu}
        />
      )}
    </li>
  );
}

// Folder header — draggable (reorder among folders) + droppable (target
// for groups being filed into it).  Click toggles fold/unfold; double-
// click renames.
function FolderHeader({
  folder,
  folded,
  renaming,
  onToggle,
  onBeginRename,
  onEndRename,
  onCancelRename,
  onDelete,
  dropIndicator,
  isDragging,
}: {
  folder: Folder;
  folded: boolean;
  renaming: boolean;
  onToggle: () => void;
  onBeginRename: () => void;
  onEndRename: (name: string) => void | Promise<void>;
  onCancelRename: () => void;
  onDelete: () => void;
  dropIndicator: "before" | "after" | "inside" | null;
  isDragging: boolean;
}) {
  const dragId = `f:${folder.id}`;
  const draggable = useDraggable({ id: dragId, disabled: renaming });
  const droppable = useDroppable({ id: dragId });
  const setRef = (el: HTMLDivElement | null) => {
    draggable.setNodeRef(el);
    droppable.setNodeRef(el);
  };
  return (
    <div
      ref={setRef}
      className={`flex items-center gap-1 rounded-md ${
        isDragging ? "opacity-40" : ""
      } ${
        dropIndicator === "before"
          ? "shadow-[inset_0_2px_0_0_#6366f1]"
          : dropIndicator === "after"
            ? "shadow-[inset_0_-2px_0_0_#6366f1]"
            : dropIndicator === "inside"
              ? "bg-indigo-50 dark:bg-indigo-950 ring-1 ring-indigo-300 dark:ring-indigo-800"
              : ""
      }`}
    >
      <button
        type="button"
        {...draggable.listeners}
        {...draggable.attributes}
        aria-label="Drag folder to reorder"
        title="Drag to reorder folders"
        className="cursor-grab touch-none rounded px-0.5 text-xs text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-300 active:cursor-grabbing"
      >
        ⋮⋮
      </button>
      <button
        type="button"
        onClick={onToggle}
        className="rounded px-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
        aria-label={folded ? "Expand folder" : "Collapse folder"}
        title={folded ? "Expand folder" : "Collapse folder"}
      >
        {folded ? "▸" : "▾"}
      </button>
      {renaming ? (
        <FolderRenameInput
          folder={folder}
          onCommit={onEndRename}
          onCancel={onCancelRename}
        />
      ) : (
        <button
          type="button"
          onClick={onToggle}
          onDoubleClick={onBeginRename}
          className="flex-1 truncate rounded-md px-1 py-1 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          title="Click to expand/collapse · double-click to rename"
        >
          {folder.name}
        </button>
      )}
      <button
        type="button"
        onClick={onDelete}
        className="rounded px-1 text-xs text-slate-400 dark:text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 pointer-fine:invisible pointer-fine:group-hover:visible"
        aria-label="Delete folder"
        title="Delete folder (groups inside move back to top level)"
      >
        ✕
      </button>
    </div>
  );
}

function MoveToFolderMenu({
  currentFolderId,
  folders,
  onPick,
  onClose,
}: {
  currentFolderId: string | null;
  folders: Folder[];
  onPick: (folderId: string | null) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute right-0 top-full z-10 mt-1 w-48 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 py-1 shadow-lg"
    >
      <button
        type="button"
        onClick={() => onPick(null)}
        className={`flex w-full items-center justify-between px-2 py-1 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800 ${
          currentFolderId === null ? "font-medium text-slate-900 dark:text-slate-100" : "text-slate-700 dark:text-slate-200"
        }`}
      >
        <span>Top level</span>
        {currentFolderId === null && <span aria-hidden>✓</span>}
      </button>
      {folders.length > 0 && (
        <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
      )}
      {folders.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onPick(f.id)}
          className={`flex w-full items-center justify-between px-2 py-1 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800 ${
            currentFolderId === f.id
              ? "font-medium text-slate-900 dark:text-slate-100"
              : "text-slate-700 dark:text-slate-200"
          }`}
        >
          <span className="truncate">{f.name}</span>
          {currentFolderId === f.id && <span aria-hidden>✓</span>}
        </button>
      ))}
      {folders.length === 0 && (
        <p className="px-2 py-1 text-[11px] italic text-slate-400 dark:text-slate-500">
          No folders yet — create one below.
        </p>
      )}
    </div>
  );
}

// Sidebar context: when a group is selected, surface its sharing state.
//   - Group I own + active outgoing shares → full "Shared with" management
//     panel (change permission, revoke), same component used in the share
//     modal so edits are consistent across surfaces.
//   - Group shared TO me                   → "Shared from @owner" + a small
//     "Leave" action so the recipient can drop the share without opening
//     /shared-with-me.
//   - Neither                              → render nothing.
function GroupSharingPanel({
  group,
  currentUserId,
  accessToken,
}: {
  group: Group | null;
  currentUserId: string;
  accessToken: string;
}) {
  const [rev, setRev] = useState(0);
  const isOwn =
    group != null && (!group.ownerId || group.ownerId === currentUserId);
  const isShared =
    group != null && !!group.ownerId && group.ownerId !== currentUserId;

  if (!group) return null;

  if (isShared) {
    return (
      <SharedFromBlock
        group={group}
        accessToken={accessToken}
        onLeft={() => setRev((n) => n + 1)}
      />
    );
  }

  if (!isOwn) return null;

  return (
    <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-3">
      <ActivityWatchToggle
        scope="group"
        scopeId={group.id}
        accessToken={accessToken}
      />
      <div className="mt-3 mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Shared with
      </div>
      <SharedWithPanel
        scope="group"
        scopeId={group.id}
        accessToken={accessToken}
        rev={rev}
        onChanged={() => setRev((n) => n + 1)}
        variant="bare"
      />
    </div>
  );
}

function SharedFromBlock({
  group,
  accessToken,
  onLeft,
}: {
  group: Group;
  accessToken: string;
  onLeft: () => void;
}) {
  const [owner, setOwner] = useState<UserBrief | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Resolve owner + the share row that gave me this group, in parallel.
  // The share lookup walks my incoming shares; the first one whose scope
  // matches this group is the one we'd revoke if the user clicks Leave.
  useEffect(() => {
    if (!group.ownerId) return;
    let alive = true;
    (async () => {
      try {
        const [u, mine] = await Promise.all([
          lookupUser(accessToken, group.ownerId),
          // Lazily import to avoid a circular tangle at module load.
          import("../shares/api").then((m) =>
            m.listShares(accessToken, "incoming"),
          ),
        ]);
        if (!alive) return;
        setOwner(u);
        const match = mine.find(
          (s) =>
            !s.revokedAt &&
            ((s.scope === "group" && s.scopeId === group.id) ||
              // item-scope share — we still surface a "Leave" if at least
              // one shared item in this group is alive, but the simpler
              // group-scope case is the common path.  Skip for item-scope
              // here since the group is in our cache via item-scope only.
              false),
        );
        setShareId(match?.id ?? null);
      } catch {
        if (alive) {
          setOwner(null);
          setShareId(null);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [group.id, group.ownerId, accessToken]);

  const label = owner?.handle
    ? `@${owner.handle}`
    : owner?.display_name?.trim() || owner?.email || "another user";

  const leave = async () => {
    if (!shareId) return;
    if (!confirm(`Leave the share for "${group.name}"?`)) return;
    setBusy(true);
    try {
      await revokeShare(accessToken, shareId);
      onLeft();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Shared from
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs">
        <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{label}</span>
        {shareId && (
          <button
            type="button"
            onClick={() => void leave()}
            disabled={busy}
            className="rounded px-1.5 py-0.5 text-[11px] text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950 disabled:opacity-50"
          >
            Leave
          </button>
        )}
      </div>
      <div className="mt-2">
        <ActivityWatchToggle
          scope="group"
          scopeId={group.id}
          accessToken={accessToken}
        />
      </div>
    </div>
  );
}
